import { AxiosResponse } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { takeLatest, takeEvery, call, put, all, select } from 'redux-saga/effects';
import { OrderTrackingMaintenanceFilter, OrderTrackingMaintenanceItem, UpdateOrderTrackingResult } from '../../api/types';
import { SimplyTestApi } from '../../api/simplytest';
import { selectConfig } from '../../shared/redux/slice';
import { IConfiguration } from '../../shared/types';
import { selectIdToken, unauthorized } from '../../auth/redux/slice';
import { toast } from 'react-hot-toast';
import trackingMaintenanceSlice, { loadPendingTracking, saveOrderTracking } from './slice';

export abstract class TrackingMaintenanceSagas {
    public static* run(){
        try {
            yield all([
                takeLatest(loadPendingTracking, TrackingMaintenanceSagas.loadPendingTracking),
                // takeEvery, not takeLatest - saving row A shouldn't cancel an in-flight save for
                // row B just because both were clicked close together.
                takeEvery(saveOrderTracking, TrackingMaintenanceSagas.saveOrderTracking),
            ]);
        }
        catch(e: any){
            console.log(`[TrackingMaintenanceSagas:run] the following exception has occurred`, e);
        }
    }

    private static* loadPendingTracking(action: PayloadAction<OrderTrackingMaintenanceFilter>){
        try {
            yield put(trackingMaintenanceSlice.actions.setPendingState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const response: AxiosResponse<OrderTrackingMaintenanceItem[]> = yield call(
                { context: client, fn: client.getPendingOrderTracking }, action.payload,
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(trackingMaintenanceSlice.actions.setPending(response.data));
            yield put(trackingMaintenanceSlice.actions.setPendingState('READY'));
        }
        catch(e: any){
            yield put(trackingMaintenanceSlice.actions.setPendingState('ERROR'));
            toast.error('Error loading orders needing tracking');
            console.log(`[TrackingMaintenanceSagas:loadPendingTracking] the following exception has occurred`, e);
        }
    }

    private static* saveOrderTracking(action: PayloadAction<{ orderId: number; returnTrack: string | null }>){
        const { orderId, returnTrack } = action.payload;
        try {
            yield put(trackingMaintenanceSlice.actions.setOrderSaving({ orderId, saving: true }));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const response: AxiosResponse<UpdateOrderTrackingResult> = yield call(
                { context: client, fn: client.updateOrderTracking }, orderId, returnTrack,
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200){
                toast.error(typeof response.data === 'string' ? response.data : `Error saving tracking # for order #${orderId}`);
                yield put(trackingMaintenanceSlice.actions.setOrderSaving({ orderId, saving: false }));
                return;
            }

            yield put(trackingMaintenanceSlice.actions.setOrderManualReturnTrack({ orderId, returnTrack: response.data.returnTrack ?? null }));
            yield put(trackingMaintenanceSlice.actions.setOrderSaving({ orderId, saving: false }));
            toast.success(`Tracking # saved for order #${orderId}`);
        }
        catch(e: any){
            yield put(trackingMaintenanceSlice.actions.setOrderSaving({ orderId, saving: false }));
            toast.error(`Error saving tracking # for order #${orderId}`);
            console.log(`[TrackingMaintenanceSagas:saveOrderTracking] the following exception has occurred`, e);
        }
    }
}
